import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { awsApi, accessRequestsApi, resourcesApi } from '../services/api';
import { Resource } from '../types';
import { Plus, Warehouse, RefreshCw, ExternalLink, Trash2, Shield, Clock, Crown, Cloud, type LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { usePermissions } from '../hooks/usePermissions';
import AccessRequestForm from '../components/AccessRequestForm';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { Link } from 'react-router-dom';
import { Banner, Breadcrumbs, Button, Card, EmptyState, IconButton, Input, Modal, PageHeader, Select, StatusBadge, Textarea, OutcomeBadge, MonoCell } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { resourceIcon } from '@/lib/resourceIcons';
import { BRAND } from '@/brand';
import ProviderMark from '@/components/ProviderMark';
import CardGridSkeleton from '@/components/CardGridSkeleton';

export default function AWSResources() {
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showAccessRequestForm, setShowAccessRequestForm] = useState(false);
  const [selectedResourceForRequest, setSelectedResourceForRequest] = useState<Resource | null>(null);
  const [showRoleMappings, setShowRoleMappings] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [pendingAccount, setPendingAccount] = useState<any>(null);
  const [awsAccessDescription, setAwsAccessDescription] = useState<string>('');
  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const { isAdmin } = usePermissions();

  // Fetch accounts
  const { data: accountsData, isLoading: accountsLoading, isError: listError, refetch: refetchList } = useQuery('aws-accounts', () => awsApi.listAccounts());
  const accounts = accountsData?.data?.accounts || [];

  // Console URL mutation — now accepts aws_role_arn
  const consoleUrlMutation = useMutation(
    (data: { aws_account_id: string; destination?: string; aws_role_arn?: string }) => awsApi.generateConsoleUrl(data),
    {
      onSuccess: (res) => { window.open(res.data.console_url, '_blank'); toast.success('AWS Console opened'); },
      onError: (err) => { toast.error(errorMessage(err, 'Failed to open console')); },
    }
  );

  // Account CRUD
  const createAccountMutation = useMutation((data: any) => awsApi.createAccount(data), {
    onSuccess: () => { toast.success('AWS account added'); queryClient.invalidateQueries('aws-accounts'); setShowAccountForm(false); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to add account')); },
  });

  const deleteAccountMutation = useMutation((id: string) => awsApi.deleteAccount(id), {
    onSuccess: () => { toast.success('Account removed'); queryClient.invalidateQueries('aws-accounts'); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to remove account')); },
  });

  // Access request checks for non-admin users
  const { data: accessChecks } = useQuery(
    ['aws-account-access', accounts.map((a: any) => a.id).join(',')],
    async () => {
      const statusMap: Record<string, { hasAccess: boolean; resourceId?: string }> = {};
      for (const acc of accounts) {
        const extId = `aws-${acc.account_id}`;
        try {
          const resp = await resourcesApi.listAll('aws_account', undefined, 100, 0);
          const match = resp.data?.resources?.find((r: any) => r.external_id === extId);
          if (match) {
            const accessResp = await resourcesApi.getAccessStatus(match.id);
            statusMap[acc.id] = { hasAccess: accessResp.data.has_access === true, resourceId: match.id };
          } else {
            statusMap[acc.id] = { hasAccess: false };
          }
        } catch { statusMap[acc.id] = { hasAccess: false }; }
      }
      return statusMap;
    },
    { enabled: accounts.length > 0 && !isAdmin(), refetchInterval: 30000 }
  );

  // Role mappings (fetch always for role picker)
  const { data: roleMappingsData } = useQuery('aws-role-mappings', () => awsApi.listRoleMappings());
  const roleMappings = roleMappingsData?.data?.mappings || [];

  const deleteMappingMutation = useMutation((id: string) => awsApi.deleteRoleMapping(id), {
    onSuccess: () => { toast.success('Mapping removed'); queryClient.invalidateQueries('aws-role-mappings'); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to remove mapping')); },
  });

  const handleOpenConsole = (account: any, awsRoleArn?: string) => {
    consoleUrlMutation.mutate({
      aws_account_id: account.id,
      destination: 'https://console.aws.amazon.com/',
      ...(awsRoleArn ? { aws_role_arn: awsRoleArn } : {}),
    });
  };

  const handleRequestAccess = async (account: any) => {
    // Show the description input modal instead of role picker
    setPendingAccount(account);
    setAwsAccessDescription('');
    setShowRolePicker(true);
  };

  const openAccessRequestForm = async (account: any) => {
    const extId = `aws-${account.account_id}`;
    try {
      const resp = await resourcesApi.listAll('aws_account', undefined, 100, 0);
      const match = resp.data?.resources?.find((r: Resource) => r.external_id === extId);
      if (match) {
        setSelectedResourceForRequest(match);
        setShowAccessRequestForm(true);
      } else toast.error('AWS account resource not found. Admin needs to re-add the account.');
    } catch (err) { toast.error(errorMessage(err, 'Failed to look up resource.')); }
  };

  const userHasAccess = (account: any): boolean => {
    if (isAdmin()) return true;
    return accessChecks?.[account.id]?.hasAccess === true;
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <PageHeader
        className="mb-6"
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Cloud Providers', href: '/cloud-providers' }, { label: 'Amazon Web Services' }]}
            renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
          />
        }
        icon={<Warehouse />}
        title="Amazon Web Services"
        description={`Access the AWS Management Console through ${BRAND.name}`}
        secondaryActions={isAdmin() ? (
          <Button variant="secondary" aria-pressed={showRoleMappings} onClick={() => setShowRoleMappings(!showRoleMappings)}
            leadingIcon={<Shield className="h-4 w-4" />}
            className={clsx(showRoleMappings && 'ring-2 ring-[var(--ds-color-ring)]')}>
            AWS Roles
          </Button>
        ) : undefined}
        primaryAction={isAdmin() ? (
          <Button onClick={() => setShowAccountForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Account
          </Button>
        ) : undefined}
      />

      {/* Account Cards */}
      {accountsLoading ? (
        <CardGridSkeleton label="Loading AWS accounts" />
      ) : listError ? (
        <Card>
          <EmptyState
            icon={<Warehouse />}
            title="Could not load AWS accounts"
            description="Something went wrong while fetching the list. Check your connection and try again."
            action={<Button variant="secondary" onClick={() => refetchList()} leadingIcon={<RefreshCw className="h-4 w-4" />}>Retry</Button>}
          />
        </Card>
      ) : accounts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Warehouse />}
            title="No AWS Accounts Configured"
            description={`Add an AWS account to enable console access through ${BRAND.name}.`}
            action={isAdmin() ? (
              <Button onClick={() => setShowAccountForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Account
              </Button>
            ) : undefined}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((acc: any) => {
            const hasAccess = userHasAccess(acc);
            return (
              <Card key={acc.id} className="group">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <ProviderMark label="AWS" />
                      <div className="min-w-0">
                        <h3 className="text-base font-medium text-[var(--d2d-text-primary)]">{acc.account_name}</h3>
                        <p className="text-xs text-[var(--d2d-text-muted)]"><MonoCell value={String(acc.account_id ?? '')} /></p>
                      </div>
                    </div>
                    {isAdmin() && (
                      <IconButton aria-label="Remove account" tooltip="Remove account" size="sm" icon={<Trash2 className="h-4 w-4" />}
                        onClick={async () => {
                          if (await confirm({
                            title: 'Remove this AWS account?',
                            message: `${acc.account_name} (${acc.account_id}) will be removed from ${BRAND.name}.`,
                            confirmLabel: 'Remove account',
                            variant: 'danger',
                          })) deleteAccountMutation.mutate(acc.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-[var(--danger-text)]" />
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-[var(--d2d-bg-secondary)]">
                    {hasAccess ? (
                      <StatusBadge value="approved">Access Granted</StatusBadge>
                    ) : (
                      <OutcomeBadge value="required" tone="new">Access Required</OutcomeBadge>
                    )}
                    {acc.regions && (
                      <span className="ml-auto text-micro font-mono text-[var(--d2d-text-muted)]">{acc.regions.join(', ')}</span>
                    )}
                  </div>

                  {/* Approved Roles (for non-admin users with access) */}
                  {hasAccess && !isAdmin() && (
                    <ApprovedRolesSection
                      accountId={acc.id}
                      onOpenConsole={(roleArn) => handleOpenConsole(acc, roleArn)}
                      onRequestMore={() => handleRequestAccess(acc)}
                      isLoading={consoleUrlMutation.isLoading}
                    />
                  )}

                  {/* Admin: simple console button */}
                  {hasAccess && isAdmin() && (
                    <Button className="w-full" onClick={() => handleOpenConsole(acc)} loading={consoleUrlMutation.isLoading}
                      leadingIcon={consoleUrlMutation.isLoading ? undefined : <ExternalLink className="h-4 w-4" />}>
                      Open AWS Console
                    </Button>
                  )}

                  {/* No access — request button */}
                  {!hasAccess && (
                    <Button variant="secondary" className="w-full" onClick={() => handleRequestAccess(acc)}
                      leadingIcon={<Clock className="h-4 w-4" />}>
                      Request Access
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Role Mappings Panel (admin only) */}
      {showRoleMappings && isAdmin() && (
        <Card className="mt-8">
          <Card.Header
            title="Available AWS Roles"
            description="AWS IAM roles that users can request access to"
            actions={
              <Button onClick={() => setShowAddMapping(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Role
              </Button>
            }
          />
          <div className="divide-y divide-[var(--d2d-border)]">
            {roleMappings.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--d2d-text-muted)]">
                No AWS roles configured. Add roles that users can request access to.
              </div>
            ) : (
              roleMappings.map((m: any) => (
                <div key={m.id} className="px-6 py-4 flex items-center justify-between hover:bg-[var(--ds-color-bg-hover)]">
                  <div className="flex items-center gap-3 min-w-0">
                    <Shield aria-hidden className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {m.aws_role_arn.split('/').pop()}
                      </div>
                      <div className="text-xs text-[var(--d2d-text-secondary)] mt-0.5">
                        <MonoCell value={m.aws_role_arn} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--d2d-text-muted)]">{m.max_session_duration}s</span>
                    <IconButton aria-label="Remove mapping" tooltip="Remove mapping" size="sm" icon={<Trash2 className="h-4 w-4" />}
                      className="hover:text-[var(--danger-text)]"
                      onClick={async () => {
                        if (await confirm({
                          title: 'Remove this mapping?',
                          message: `${m.aws_role_arn} will no longer be available to request.`,
                          confirmLabel: 'Remove mapping',
                          variant: 'danger',
                        })) deleteMappingMutation.mutate(m.id);
                      }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* Info Banner */}
      <Banner variant="info" title="How AWS Access Works" className="mt-8">
        {isAdmin()
          ? 'As an admin, you can access the AWS Console directly. Use AWS Roles to register specific IAM roles (e.g., S3-only, EC2) that users can request access to.'
          : 'Click "Request Access" to submit an access request. Once approved by an admin, you can open the AWS Console with permissions defined by your role mapping.'}
      </Banner>

      {/* Modals */}
      {showAccountForm && (
        <AddAccountModal onClose={() => setShowAccountForm(false)}
          onSubmit={(data) => createAccountMutation.mutate(data)} isLoading={createAccountMutation.isLoading} />
      )}

      {showAddMapping && (
        <AddRoleMappingModal accounts={accounts} onClose={() => setShowAddMapping(false)}
          onSuccess={() => { queryClient.invalidateQueries('aws-role-mappings'); setShowAddMapping(false); }} />
      )}

      {showRolePicker && pendingAccount && (
        <AWSAccessDescriptionModal
          accountName={pendingAccount.account_name}
          onSubmit={async (description: string) => {
            setShowRolePicker(false);
            setAwsAccessDescription(description);
            await openAccessRequestForm(pendingAccount);
            setPendingAccount(null);
          }}
          onClose={() => { setShowRolePicker(false); setPendingAccount(null); }}
        />
      )}

      {showAccessRequestForm && (
        <AccessRequestForm resource={selectedResourceForRequest}
          metadata={awsAccessDescription ? { aws_access_description: awsAccessDescription } : undefined}
          onClose={() => { setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setAwsAccessDescription(''); }}
          onSuccess={() => {
            queryClient.invalidateQueries('aws-account-access');
            setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setAwsAccessDescription('');
            toast.success('Access request submitted!');
          }}
        />
      )}
    </div>
  );
}

// ===== Add Account Modal =====
function AddAccountModal({ onClose, onSubmit, isLoading }: { onClose: () => void; onSubmit: (data: any) => void; isLoading: boolean }) {
  const formId = useId();
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [assumeRoleArn, setAssumeRoleArn] = useState('');
  const [regions, setRegions] = useState('ap-south-1');
  const [externalId, setExternalId] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      account_id: accountId, account_name: accountName, assume_role_arn: assumeRoleArn,
      regions: regions.split(',').map((r) => r.trim()).filter(Boolean),
      external_id: externalId || undefined,
      access_key_id: accessKeyId || undefined,
      secret_access_key: secretAccessKey || undefined,
    });
  };

  return (
    <Modal open onClose={onClose} title="Add AWS Account" size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading}>{isLoading ? 'Adding...' : 'Add Account'}</Button>
        </>
      }>
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Input label="Account ID" placeholder="123456789012" value={accountId} onChange={(e) => setAccountId(e.target.value)} required />
        <Input label="Account Name" placeholder="Production" value={accountName} onChange={(e) => setAccountName(e.target.value)} required />
        <Input label="Access Key ID" className="[&_input]:font-mono" placeholder="AKIAIOSFODNN7EXAMPLE" value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)} />
        <Input label="Secret Access Key" className="[&_input]:font-mono" type="password" placeholder="••••••••" value={secretAccessKey} onChange={(e) => setSecretAccessKey(e.target.value)} />
        <Input label="Assume Role ARN" className="[&_input]:font-mono" placeholder="arn:aws:iam::123456789012:role/IAP-Access" value={assumeRoleArn} onChange={(e) => setAssumeRoleArn(e.target.value)} required />
        <Input label="Regions (comma-separated)" placeholder="ap-south-1" value={regions} onChange={(e) => setRegions(e.target.value)} />
        <Input label="External ID (optional)" placeholder="Optional" value={externalId} onChange={(e) => setExternalId(e.target.value)} />
      </form>
    </Modal>
  );
}

// ===== Add Role Mapping Modal =====
function AddRoleMappingModal({ accounts, onClose, onSuccess }: { accounts: any[]; onClose: () => void; onSuccess: () => void }) {
  const formId = useId();
  const [awsAccountId, setAwsAccountId] = useState(accounts[0]?.id || '');
  const [awsRoleArn, setAwsRoleArn] = useState('');
  const [maxDuration, setMaxDuration] = useState('3600');

  const createMutation = useMutation(
    () => awsApi.createRoleMapping({ iap_role_id: '', aws_account_id: awsAccountId, aws_role_arn: awsRoleArn, max_session_duration: parseInt(maxDuration) }),
    { onSuccess: () => { toast.success('Mapping created'); onSuccess(); }, onError: (err) => { toast.error(errorMessage(err, 'Failed to create mapping')); } }
  );

  return (
    <Modal open onClose={onClose} title="Add AWS Role" size="md"
      description="Register an AWS IAM role that users can request access to"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={createMutation.isLoading}>
            {createMutation.isLoading ? 'Creating...' : 'Add Role'}
          </Button>
        </>
      }>
      <form id={formId} onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
        <Select label="AWS Account" value={awsAccountId} onChange={(e) => setAwsAccountId(e.target.value)} required>
          {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.account_name} ({a.account_id})</option>)}
        </Select>
        <Input label="AWS IAM Role ARN" className="[&_input]:font-mono" placeholder="arn:aws:iam::123456:role/S3ReadOnly" value={awsRoleArn}
          onChange={(e) => setAwsRoleArn(e.target.value)} required
          help="The AWS IAM role users will assume (e.g., S3-only, EC2 read-only)" />
        <Input label="Max Session Duration (seconds)" type="number" min="900" max="43200" value={maxDuration} onChange={(e) => setMaxDuration(e.target.value)} />
      </form>
    </Modal>
  );
}

// ===== Approved Roles Section (shown on account card) =====
function ApprovedRolesSection({ accountId, onOpenConsole, onRequestMore, isLoading }: {
  accountId: string;
  onOpenConsole: (roleArn: string) => void;
  onRequestMore: () => void;
  isLoading: boolean;
}) {
  const { data } = useQuery(
    ['aws-approved-roles', accountId],
    () => awsApi.getApprovedRoles(accountId),
    { refetchInterval: 30000 }
  );

  const roles = data?.data?.roles || [];

  // Helper: map role names to the service icon (lib/resourceIcons).
  const getRoleIcon = (name: string): LucideIcon => {
    const n = name.toLowerCase();
    if (n.includes('admin') || n.includes('full')) return Crown;
    for (const svc of ['s3', 'ec2', 'lambda', 'rds', 'eks', 'ecs', 'route53', 'cloudfront', 'iam', 'dynamodb', 'sqs', 'sns']) {
      if (n.includes(svc)) return resourceIcon(`aws_${svc}`);
    }
    if (n.includes('dns')) return resourceIcon('aws_route53');
    if (n.includes('cdn')) return resourceIcon('aws_cloudfront');
    return Cloud;
  };

  // Helper: time remaining
  const getTimeRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hrs = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hrs > 0) return `${hrs}h ${mins}m left`;
    return `${mins}m left`;
  };

  if (roles.length === 0) {
    // No specific roles — show a simple open button (uses default role)
    return (
      <div className="space-y-2">
        <Button className="w-full" onClick={() => onOpenConsole('')} loading={isLoading}
          leadingIcon={isLoading ? undefined : <ExternalLink className="h-4 w-4" />}>
          Open AWS Console
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Approved role buttons */}
      {roles.map((role) => (
        <Button key={role.request_id} className="w-full"
          onClick={() => onOpenConsole(role.aws_role_arn)}
          loading={isLoading}
          leadingIcon={(() => { const RoleIcon = getRoleIcon(role.role_name); return <RoleIcon className="h-4 w-4" aria-hidden="true" />; })()}
          trailingIcon={<ExternalLink className="h-3.5 w-3.5" />}>
          <span className="truncate">Open {role.role_name}</span>
          <span className="text-micro font-normal opacity-80">{getTimeRemaining(role.expires_at)}</span>
        </Button>
      ))}

      {/* Request More Access */}
      <Button variant="ghost" size="sm" className="w-full" onClick={onRequestMore} leadingIcon={<Plus className="h-3.5 w-3.5" />}>
        Request More Access
      </Button>
    </div>
  );
}

// ===== AWS Access Description Modal =====
function AWSAccessDescriptionModal({ accountName, onSubmit, onClose }: {
  accountName: string;
  onSubmit: (description: string) => void;
  onClose: () => void;
}) {
  const [description, setDescription] = useState('');

  return (
    <Modal open onClose={onClose} title="Request AWS Access" size="md"
      description={<>Describe the access you need for <span className="font-medium">{accountName}</span></>}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { if (description.trim()) onSubmit(description.trim()); }}
            disabled={!description.trim()}
          >
            Continue
          </Button>
        </>
      }>
      <div className="space-y-4">
        <Textarea
          label="What AWS access do you need?"
          rows={4}
          resize="none"
          placeholder="e.g., I need access to S3 buckets for data migration, or EC2 instances for deployment..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autoFocus
          help="The admin will review your request and assign the appropriate AWS role."
        />

        {/* Info box */}
        <Banner variant="info">
          Be specific about the services and resources you need (e.g., "S3 read/write access to production-data bucket" or "EC2 instance management in ap-south-1").
        </Banner>
      </div>
    </Modal>
  );
}
